import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Building2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { api } from '../utils/api';

const INSURANCE_PROVIDERS = [
  "UnitedHealthcare",
  "Elevance Health (Anthem)",
  "Centene Corporation",
  "Humana Inc.",
  "CVS Health (Aetna)",
  "The Cigna Group",
  "Blue Cross Blue Shield",
  "Kaiser Permanente",
  "Molina Healthcare",
  "Oscar Health",
  "HealthMarkets",
  "Medical Mutual",
  "Mutual of Omaha",
  "CareSource",
  "Highmark",
  "Blue Shield of California",
  "Florida Blue",
  "Horizon Blue Cross Blue Shield",
  "Premera Blue Cross",
  "Tufts Health Plan"
];

const InsuranceModal = ({ open, onClose, patient, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearchTerm(patient?.insurance_provider || '');
      setSelectedProvider(patient?.insurance_provider || '');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, patient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProviders = INSURANCE_PROVIDERS.filter(provider =>
    provider.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    setSearchTerm(provider);
    setShowDropdown(false);
  };

  const handleSave = async () => {
    if (!selectedProvider.trim()) {
      toast.error('Please select an insurance provider');
      return;
    }

    try {
      setSaving(true);
      await api.updatePatientInsurance(patient.id, selectedProvider);
      toast.success('Insurance provider updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update insurance provider');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        data-testid="insurance-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {patient?.insurance_provider ? 'Update Insurance' : 'Add Insurance Provider'}
              </h2>
              <p className="text-sm text-gray-500">
                {patient?.first_name} {patient?.last_name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="rounded-full"
            data-testid="close-insurance-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Insurance Provider / Payor
          </label>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search insurance providers..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedProvider(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                className="pl-10 pr-4"
                data-testid="insurance-search-input"
              />
            </div>

            {/* Dropdown */}
            {showDropdown && (
              <div 
                ref={dropdownRef}
                className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto"
                data-testid="insurance-dropdown"
              >
                {filteredProviders.length > 0 ? (
                  filteredProviders.map((provider) => (
                    <button
                      key={provider}
                      className={`w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center justify-between transition-colors ${
                        selectedProvider === provider ? 'bg-purple-50' : ''
                      }`}
                      onClick={() => handleSelectProvider(provider)}
                      data-testid={`insurance-option-${provider.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className="text-sm">{provider}</span>
                      {selectedProvider === provider && (
                        <Check className="w-4 h-4 text-purple-600" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No providers found. You can still use &ldquo;{searchTerm}&rdquo; as a custom provider.
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedProvider && (
            <p className="mt-2 text-sm text-gray-600">
              Selected: <span className="font-medium text-purple-600">{selectedProvider}</span>
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full"
            data-testid="cancel-insurance-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !selectedProvider.trim()}
            className="rounded-full"
            style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
            data-testid="save-insurance-btn"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceModal;
